import { useEffect, useState } from 'react';
import {
  getLlmClient,
  SUPPORTED_MODELS,
  type EngineState,
  type LlmProvider,
} from '../../lib/llm';

interface Props {
  enabled: boolean;
  provider: LlmProvider;
  geminiKeySet: boolean;
  modelId: string;
  deviceUnsupported: boolean;
  onEnableChange: (enabled: boolean) => void;
  onModelChange: (modelId: string) => void;
  onTryAnyway: () => void;
}

export function LlmStatusCard({
  enabled,
  provider,
  geminiKeySet,
  modelId,
  deviceUnsupported,
  onEnableChange,
  onModelChange,
  onTryAnyway,
}: Props) {
  const client = getLlmClient();
  const [state, setState] = useState<EngineState>(client.getState());
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Re-sync immediately: when the provider switches, `client` is a
    // different instance — without this the card would keep showing the
    // previous engine's last state (e.g. a stale on-device GPU-OOM error
    // displayed inside the Gemini card).
    setState(client.getState());
    const unsubscribe = client.subscribe(setState);
    return () => {
      unsubscribe();
    };
  }, [client]);

  const model = SUPPORTED_MODELS.find((m) => m.id === modelId) ?? SUPPORTED_MODELS[0];

  if (!enabled) {
    return (
      <section className="ms-card">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h2 className="ms-title">
              {provider === 'gemini' ? 'Cloud LLM enrichment' : 'Local LLM enrichment'}
            </h2>
            <p className="mt-0.5 text-xs text-slate-500">
              Optional. Adds per-item compliance grades, spin classification, and a rationale
              draft button.{' '}
              {provider === 'gemini'
                ? 'Uses Google Gemini with your own API key (sends manuscript excerpts to Google).'
                : `${model.approxDownloadGB.toFixed(1)} GB one-time on-device download — nothing leaves your device.`}
            </p>
          </div>
          <button
            type="button"
            onClick={() => onEnableChange(true)}
            className="ms-btn ms-btn-primary ms-btn-xs whitespace-nowrap"
          >
            Enable
          </button>
        </div>
      </section>
    );
  }

  // Opt-in cloud engine (Gemini, BYOK). No download / WebGPU; the on-device
  // device-unsupported path does not apply. The data-egress consent is shown
  // here every time the engine is active, not just once.
  if (provider === 'gemini') {
    return (
      <section className="ms-card">
        <h2 className="ms-title">Cloud LLM enrichment · Gemini</h2>
        <p className="mt-0.5 text-xs text-slate-500">
          Google Gemini 2.5 Flash · your API key (BYOK)
        </p>
        <div className="mt-2 rounded-lg border border-amber-300 bg-amber-50 px-2.5 py-2 text-[11px] leading-relaxed text-amber-900">
          <strong>Cloud mode:</strong> manuscript excerpts are sent to Google to generate
          enrichment. This is the only engine that sends text off your device. Google&rsquo;s
          free tier may use prompts for training — prefer the on-device engine for unpublished
          work, or a paid Google tier.
        </div>
        {!geminiKeySet ? (
          <p className="mt-2 text-xs text-slate-700">
            No API key yet. Add your Google Gemini key in <span className="font-medium">
              Settings
            </span>{' '}
            to use this engine. The pattern engine runs regardless.
          </p>
        ) : state.kind === 'error' ? (
          <div className="mt-2 rounded border border-red-300 bg-red-50 px-2 py-1 text-[11px] text-red-900">
            {state.message}
          </div>
        ) : (
          <p className="mt-2 text-xs text-emerald-700">
            ● Key set. Cloud enrichment will run automatically on the next manuscript.
          </p>
        )}
        <button
          type="button"
          onClick={() => onEnableChange(false)}
          className="mt-2 text-[10px] text-slate-400 hover:text-slate-700"
        >
          Disable LLM enrichment
        </button>
      </section>
    );
  }

  // Graceful, non-nagging fallback: this device can't run the on-device
  // model (no WebGPU, or GPU OOM / device-lost). Calm neutral note — the
  // pattern engine is the product; the AI is optional polish.
  if (deviceUnsupported) {
    return (
      <section className="ms-card">
        <h2 className="ms-title">Local LLM enrichment</h2>
        <p className="mt-1 text-xs text-slate-600">
          This device can’t run the optional on-device AI (it needs a GPU with more memory
          than is available here). <strong>ManuscriptSkim works fully without it</strong> — the
          AI only adds optional graded judgments on top of the pattern-engine results.
        </p>
        <div className="mt-2 flex items-center gap-3">
          <button
            type="button"
            onClick={onTryAnyway}
            className="text-[10px] text-slate-500 underline hover:text-slate-700"
          >
            Try anyway
          </button>
          <button
            type="button"
            onClick={() => onEnableChange(false)}
            className="text-[10px] text-slate-400 hover:text-slate-700"
          >
            Disable LLM enrichment
          </button>
        </div>
      </section>
    );
  }

  async function load() {
    setIsLoading(true);
    try {
      await client.ensureLoaded(modelId);
    } catch {
      // Error surfaced via state subscription.
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <section className="ms-card">
      <h2 className="ms-title">Local LLM enrichment</h2>
      <p className="mt-0.5 text-xs text-slate-500">{model.name}</p>

      {(state.kind === 'idle' || state.kind === 'error') && (
        <div className="mt-2">
          <label className="text-[10px] uppercase tracking-wide text-slate-500">Model</label>
          <select
            value={modelId}
            onChange={(e) => onModelChange(e.target.value)}
            disabled={isLoading}
            className="mt-0.5 w-full rounded border border-slate-300 px-2 py-1 text-xs"
          >
            {SUPPORTED_MODELS.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name} · ~{m.approxDownloadGB.toFixed(1)} GB
              </option>
            ))}
          </select>
          <p className="mt-0.5 text-[10px] text-slate-500">
            Smaller models use far less GPU memory. If load/inference fails with an
            out-of-memory or device-lost error, choose <strong>Phi-3.5 Mini</strong>.
          </p>
        </div>
      )}

      {state.kind === 'idle' && (
        <div className="mt-2">
          <p className="text-xs text-slate-700">
            Model not loaded yet. First load downloads ~{model.approxDownloadGB.toFixed(1)} GB and
            takes several minutes. Cached after that.
          </p>
          <button
            type="button"
            onClick={load}
            disabled={isLoading}
            className="mt-2 rounded bg-slate-700 px-2 py-1 text-[11px] text-white disabled:bg-slate-300"
          >
            Download &amp; load
          </button>
        </div>
      )}

      {state.kind === 'loading' && (
        <div className="mt-2">
          <p className="text-xs text-slate-700">
            Loading… {Math.round(state.progress * 100)}%
          </p>
          <p className="mt-0.5 truncate text-[10px] text-slate-500" title={state.text}>
            {state.text}
          </p>
          <div className="mt-1 h-1.5 w-full rounded bg-slate-200">
            <div
              className="h-1.5 rounded bg-slate-700"
              style={{ width: `${Math.max(0, Math.min(100, state.progress * 100))}%` }}
            />
          </div>
        </div>
      )}

      {state.kind === 'ready' && (
        <div className="mt-2 flex items-start justify-between gap-2">
          <p className="text-xs text-emerald-700">
            ● Loaded. LLM enrichment will run automatically on the next manuscript.
          </p>
          <button
            type="button"
            onClick={() => void client.unload()}
            className="text-[10px] text-slate-400 hover:text-slate-700"
          >
            unload
          </button>
        </div>
      )}

      {state.kind === 'absent' && (
        <div className="mt-2 rounded border border-amber-300 bg-amber-50 px-2 py-1 text-[11px] text-amber-900">
          {state.reason} The pattern-only layer still runs.
        </div>
      )}

      {state.kind === 'error' && (
        <div className="mt-2 rounded border border-red-300 bg-red-50 px-2 py-1 text-[11px] text-red-900">
          {state.message}
          <button
            type="button"
            onClick={load}
            className="ml-2 underline"
          >
            retry
          </button>
        </div>
      )}

      <button
        type="button"
        onClick={() => onEnableChange(false)}
        className="mt-2 text-[10px] text-slate-400 hover:text-slate-700"
      >
        Disable LLM enrichment
      </button>
    </section>
  );
}
